import { useEffect } from 'react';
import {
  NavigateFunction,
  useInRouterContext,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import {
  ROUTE_BOOK_ADD_ONS,
  ROUTE_BOOK_PAYMENT,
  ROUTE_BOOK_ROOT,
  ROUTE_BOOK_SCHEDULE,
  ROUTE_BOOK_SERVICES,
} from './constants';
import {
  CONSUMER_CAR,
  CONSUMER_DAY,
  CONSUMER_DETAILS,
  CONSUMER_PLAN,
} from '../consumer/paths';

/**
 * Moving between booking steps, for every tenant.
 *
 * - In-app Back (QA 2026-09-25 #9): it used to push the previous step as a
 *   NEW history entry, so the browser's own Back from there went forward
 *   again. When the step we are going back to is the page we came from,
 *   Back now steps back through history; otherwise it goes there as before.
 * - Scroll (QA #42): a new step opens at its top. The router kept the old
 *   step's offset, which landed phone users in the footer. Browser Back /
 *   Forward (POP) is left to the browser's own scroll restoration.
 */

let currentPath: string | null = null;
let previousPath: string | null = null;
/**
 * The page at each history entry of this tab (the router's `idx` in
 * window.history.state), so Back can tell whether the entry right before
 * this one is the step it wants. Only knowing the last page was not
 * enough (owner, 2026-10-29): after one Back the "last page" was the step
 * ahead, the next Back pushed a new entry, and the browser's own Back then
 * walked forward through the steps again.
 */
const pathAtIndex = new Map<number, string>();
const INDEX_KEY = 'apr.stepPaths';

const historyIndex = (): number | null => {
  const idx = window.history.state?.idx;
  return typeof idx === 'number' ? idx : null;
};

const loadIndex = () => {
  if (pathAtIndex.size) return;
  try {
    const raw = window.sessionStorage.getItem(INDEX_KEY);
    if (!raw) return;
    Object.entries(JSON.parse(raw) as Record<string, string>).forEach(
      ([k, v]) => pathAtIndex.set(Number(k), v),
    );
  } catch (e) {
    // a reload then just goes to the step directly
  }
};

const saveIndex = () => {
  try {
    window.sessionStorage.setItem(
      INDEX_KEY, JSON.stringify(Object.fromEntries(pathAtIndex)),
    );
  } catch (e) {
    // private mode: kept in memory only
  }
};

/** Called on every location change (StepNavigationTracker). */
export function recordPath(pathname: string): void {
  const idx = historyIndex();
  if (idx !== null) {
    loadIndex();
    pathAtIndex.set(idx, pathname);
    saveIndex();
  }
  if (pathname === currentPath) return;
  previousPath = currentPath;
  currentPath = pathname;
}

/** The page before this one in this visit, if known. */
export function previousStepPath(): string | null {
  return previousPath;
}

/**
 * Back to `target`: one step back through history when the entry before
 * this one is that step; otherwise this entry is replaced by it, so the
 * browser's Back never walks forward through the steps again.
 */
export function goBackTo(navigate: NavigateFunction, target: string): void {
  const idx = historyIndex();
  if (idx !== null) {
    loadIndex();
    if (idx > 0 && pathAtIndex.get(idx - 1) === target) {
      navigate(-1);
      return;
    }
    navigate(target, { replace: true });
    return;
  }
  if (previousPath === target && window.history.length > 1) {
    navigate(-1);
    return;
  }
  navigate(target);
}

/** For tests. */
export function resetStepNavigation(): void {
  currentPath = null;
  previousPath = null;
  pathAtIndex.clear();
  try { window.sessionStorage.removeItem(INDEX_KEY); } catch (e) { /* none */ }
}

export function isBookingStep(pathname: string): boolean {
  return pathname === ROUTE_BOOK_ROOT
    || pathname.startsWith(`${ROUTE_BOOK_ROOT}/`);
}

/** The steps where a booking is under way (address picked, not yet made). */
export const BOOKING_IN_PROGRESS_STEPS = [
  ROUTE_BOOK_SERVICES,
  ROUTE_BOOK_ADD_ONS,
  ROUTE_BOOK_SCHEDULE,
  ROUTE_BOOK_PAYMENT,
  // The consumer skin's steps (Aperio).
  CONSUMER_CAR,
  CONSUMER_PLAN,
  CONSUMER_DAY,
  CONSUMER_DETAILS,
];

/**
 * Attributes for a link out to the marketing site from the portal's header
 * and footer. In the middle of a booking it opens in a new tab, so the
 * booking stays where it is (QA 2026-09-25 #26: the language pill and the
 * nav links threw the visitor out of the flow); everywhere else it is the
 * same-tab link it always was. The logo is left out on purpose: it is the
 * visitor choosing to go back to the site.
 */
export function useMarketingLinkProps(): { target?: string; rel?: string } {
  // Outside a router (a test rendering the footer alone) there is no
  // booking to protect. Stable for a component's life, so the hook order
  // never changes.
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const pathname = inRouter ? useLocation().pathname : '';
  return BOOKING_IN_PROGRESS_STEPS.includes(pathname)
    ? { target: '_blank', rel: 'noopener' }
    : {};
}

export default function StepNavigationTracker() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    const from = currentPath;
    recordPath(pathname);
    if (
      navigationType !== 'POP'
      && from !== null
      && from !== pathname
      && isBookingStep(pathname)
    ) {
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);
  return null;
}
